import React from 'react';
import { Shield, Lock, Eye, Database, UserCheck } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 md:py-20">
      <SEOHead
        title="Privacy Policy | Alden Technologies"
        description="Read the Alden Technologies privacy policy covering how we collect, use and protect your personal information when you use our website and services."
        path="/privacy-policy"
      />
      <Breadcrumbs items={[{ label: 'Privacy Policy' }]} />
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-12">
          <div className="text-center mb-8 md:mb-12">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last Updated: May 2025</p>
          </div>

          <div className="prose prose-lg max-w-none space-y-6">
            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-6 h-6 text-emerald-600" />
                Information We Collect
              </h2>
              <p className="text-gray-700 mb-4">
                Alden Technologies collects information to provide better services to our customers. We collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Personal Information:</strong> Name, email address, phone number, company name, and billing address when you request quotes, place orders, or contact us</li>
                <li><strong>Business Information:</strong> GST number, company registration details for B2B transactions</li>
                <li><strong>Payment Information:</strong> Payment details processed securely through PayU payment gateway for machinery orders, advance bookings, and services</li>
                <li><strong>Project Information:</strong> Plant capacity requirements, location details, technical specifications for customized solutions</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information, and website usage patterns</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6 text-emerald-600" />
                How We Use Your Information
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Processing orders for mineral water plants, bottling machinery, and equipment</li>
                <li>Providing quotations and technical consultations</li>
                <li>Processing online payments for advance bookings, machinery orders, and services</li>
                <li>Installation, commissioning, and after-sales support</li>
                <li>Sending order updates, service reminders, and important communications</li>
                <li>Improving our products and services based on customer feedback</li>
                <li>Compliance with legal and regulatory requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Database className="w-6 h-6 text-emerald-600" />
                Data Security
              </h2>
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your personal and payment information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>SSL encryption for all data transmission</li>
                <li>Secure payment processing through PCI-DSS compliant PayU gateway</li>
                <li>Regular security audits and updates</li>
                <li>Restricted access to customer data</li>
                <li>Secure data storage and backup systems</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Payment Information</h2>
              <p className="text-gray-700 mb-4">
                All online payments are processed through PayU, a secure and trusted payment gateway. We do not store complete credit/debit card information on our servers. Payment data is encrypted and handled according to PCI-DSS standards.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Information Sharing</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Payment processors (PayU) for transaction processing</li>
                <li>Logistics partners for equipment delivery and installation</li>
                <li>Government authorities when legally required</li>
                <li>Service providers who assist in business operations under confidentiality agreements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <UserCheck className="w-6 h-6 text-emerald-600" />
                Your Rights
              </h2>
              <p className="text-gray-700 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Access your personal data</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your data (subject to legal obligations)</li>
                <li>Opt-out of marketing communications</li>
                <li>Lodge a complaint with relevant authorities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Cookies</h2>
              <p className="text-gray-700">
                We use cookies to improve user experience, analyze website traffic, and remember your preferences. You can control cookie settings through your browser.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">For privacy-related queries:</p>
              <div className="bg-emerald-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">Alden Technologies</p>
                <p className="text-gray-700">KH No 588, Machara Harmada, Jaipur, Rajasthan - 302013</p>
                <p className="text-gray-700">Email: aldentechnologiesindia@gmail.com</p>
                <p className="text-gray-700">Phone: +91-9351292512</p>
                <p className="text-gray-700">GST: 08BUJPR0377M1ZY</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Updates to Privacy Policy</h2>
              <p className="text-gray-700">
                We may update this privacy policy periodically. Changes will be posted on this page with an updated revision date. Continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;